import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useOperator } from "@/context/OperatorContext";
import api, { formatApiError } from "@/lib/api";
import { FileUpload } from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, Radio, Loader2, Copy, RefreshCw, Wifi, WifiOff, Palette, Home, Plus, Trash2, Star, Database, Cpu, MapPinned } from "lucide-react";
import { toast } from "sonner";

function Cap({ ok }) {
  return ok ? <Check className="h-4 w-4 text-status-available" /> : <X className="h-4 w-4 text-muted-foreground" />;
}

function fmtAge(s) {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

function _ageSec(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 1000;
}

const DIAG_STATUS = {
  ok: { label: "Live — receiving data", cls: "bg-status-available/15 text-status-available" },
  rate_limited: { label: "Rate-limited by vendor", cls: "bg-status-overdue/15 text-status-overdue" },
  http_error: { label: "Connection error", cls: "bg-status-recovery/15 text-status-recovery" },
  bad_payload: { label: "Unexpected response", cls: "bg-status-recovery/15 text-status-recovery" },
  never: { label: "No fetch yet", cls: "bg-muted text-muted-foreground" },
};

export default function SettingsPage() {
  const { user } = useAuth();
  const { featureEnabled, reload } = useOperator();
  const [geo, setGeo] = useState({ lat: "", long: "", radius_km: "" });
  const [brand, setBrand] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [providers, setProviders] = useState([]);
  const [esc, setEsc] = useState(null);
  const [maint, setMaint] = useState(null);
  const [pay, setPay] = useState(null);
  const [webhook, setWebhook] = useState(null);
  const [pm, setPm] = useState(null);
  const [live, setLive] = useState(null);
  const [hubs, setHubs] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [ft, setFt] = useState(null);
  const [ftLink, setFtLink] = useState("");
  const [ftDefault, setFtDefault] = useState(true);
  const [ftSaving, setFtSaving] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [showPush, setShowPush] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [g, p, e] = await Promise.all([
        api.get("/operator/geofence"),
        api.get("/operator/telematics/providers"),
        api.get("/operator/escalation-config"),
      ]);
      if (g.data.geofence) setGeo(g.data.geofence);
      setProviders(p.data.providers);
      setEsc(e.data);
    })();
    api.get("/operator/maintenance-config").then(({ data }) => setMaint(data)).catch(() => {});
    api.get("/operator/payment-config").then(({ data }) => setPay(data)).catch(() => {});
    api.get("/operator/telematics/webhook-config").then(({ data }) => setWebhook(data)).catch(() => {});
    api.get("/operator/payment-methods").then(({ data }) => setPm(data)).catch(() => {});
    api.get("/operator/live-config").then(({ data }) => setLive(data)).catch(() => {});
    api.get("/operator/hubs").then(({ data }) => setHubs(data.hubs || [])).catch(() => {});
    api.get("/operator/telematics/fleettrack-config").then(({ data }) => { setFt(data); setFtDefault(data.is_default ?? true); }).catch(() => {});
    api.get("/operator/branding").then(({ data }) => setBrand(data)).catch(() => {});
    if (featureEnabled?.("telemetry")) loadDiag(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadLogo = async (fileId) => {
    if (!fileId) return;
    setSavingBrand(true);
    try {
      const { data } = await api.patch("/operator/branding", { logo_file: fileId, recolor: true });
      setBrand(data); await reload();
      toast.success("Logo saved — theme recoloured to match your brand");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingBrand(false); }
  };

  const saveBrandText = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.patch("/operator/branding", {
        app_title: brand.app_title, logo_text: brand.logo_text, tagline: brand.tagline, recolor: false,
      });
      setBrand(data); await reload();
      toast.success("Branding saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingBrand(false); }
  };

  const removeLogo = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.patch("/operator/branding", { clear_logo: true });
      setBrand(data); await reload();
      toast.success("Logo removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingBrand(false); }
  };

  const saveLive = async (patch) => {
    setSaving(true);
    const next = { ...live, ...patch };
    setLive(next);
    try {
      await api.patch("/operator/live-config", next);
      toast.success("Live board settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const saveGeo = async () => {
    setSaving(true);
    try {
      await api.patch("/operator/geofence", { lat: Number(geo.lat), long: Number(geo.long), radius_km: Number(geo.radius_km) });
      toast.success("Geofence updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const addHub = () => setHubs([...(hubs || []), { id: "", name: "", address: "", lat: "", long: "", is_main: !(hubs || []).length }]);
  const updateHub = (i, patch) => setHubs(hubs.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeHub = (i) => setHubs(hubs.filter((_, idx) => idx !== i));
  const setMainHub = (i) => setHubs(hubs.map((h, idx) => ({ ...h, is_main: idx === i })));
  const saveHubs = async () => {
    setSaving(true);
    try {
      const payload = (hubs || []).map((h) => ({
        id: h.id || undefined, name: (h.name || "").trim(), address: h.address || "",
        lat: Number(h.lat), long: Number(h.long), is_main: !!h.is_main,
      }));
      if (payload.some((h) => !h.name)) { toast.error("Every hub needs a name"); setSaving(false); return; }
      if (payload.some((h) => Number.isNaN(h.lat) || Number.isNaN(h.long))) { toast.error("Every hub needs valid coordinates"); setSaving(false); return; }
      const { data } = await api.patch("/operator/hubs", { hubs: payload });
      setHubs(data.hubs || []);
      toast.success("Hub locations saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const centerGeofenceOn = (h) => {
    setGeo((g) => ({ ...g, lat: h.lat, long: h.long, radius_km: g.radius_km || 8 }));
    toast.success(`Geofence centred on ${h.name} — click Save geofence to apply`);
  };

  const loadDiag = async (force) => {
    setDiagLoading(true);
    try {
      const { data } = await api.get(`/operator/telematics/diagnostics${force ? "?refresh=true" : ""}`);
      setDiag(data);
    } catch (e) { if (force) toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDiagLoading(false); }
  };

  const saveFleetTrack = async () => {
    if (!ftLink.trim()) { toast.error("Paste your gpstrack link first"); return; }
    setFtSaving(true);
    try {
      const { data } = await api.patch("/operator/telematics/fleettrack-config", { link: ftLink.trim(), set_default: ftDefault });
      setFt(data); setFtLink("");
      const t = data.test || {};
      if (t.status === "ok") toast.success(`FleetTrack connected — ${t.record_count} devices in feed`);
      else if (t.status === "rate_limited") toast.success("Saved. Vendor is rate-limiting right now — data will appear within ~2 min.");
      else toast.warning(`Saved, but test fetch said: ${t.detail || t.status}`);
      if (featureEnabled?.("telemetry")) loadDiag(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setFtSaving(false); }
  };

  const clearFleetTrack = async () => {
    setFtSaving(true);
    try {
      const { data } = await api.patch("/operator/telematics/fleettrack-config", { clear: true });
      setFt(data);
      toast.success("Reverted to default (environment) credentials");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setFtSaving(false); }
  };

  const syncFleet = async () => {
    setFtSaving(true);
    try {
      const { data } = await api.post("/operator/telematics/sync-fleet");
      const c = data.created?.length || 0, u = data.updated?.length || 0;
      toast.success(`${data.total_devices} live devices synced — ${c} added, ${u} re-paired. Fleet: ${data.fleet_size}.`);
      await reload();
      if (featureEnabled?.("telemetry")) loadDiag(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setFtSaving(false); }
  };

  const saveEsc = async () => {
    setSaving(true);
    try {
      await api.patch("/operator/escalation-config", {
        warn_hours: Number(esc.warn_hours), watch_hours: Number(esc.watch_hours),
        disable_hours: Number(esc.disable_hours), recovery_hours: Number(esc.recovery_hours),
        exposure_trigger: Number(esc.exposure_trigger),
      });
      toast.success("Escalation policy updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const saveMaint = async () => {
    setSaving(true);
    try {
      await api.patch("/operator/maintenance-config", {
        service_interval_km: Number(maint.service_interval_km),
        service_interval_days: Number(maint.service_interval_days),
        deep_discharge_pct: Number(maint.deep_discharge_pct),
      });
      toast.success("Maintenance policy updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const savePay = async () => {
    setSaving(true);
    try {
      await api.patch("/operator/payment-config", { upi_vpa: pay.upi_vpa, payee_name: pay.payee_name });
      toast.success("UPI payment details updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const webhookUrl = webhook?.token
    ? `${process.env.REACT_APP_BACKEND_URL}${webhook.path}?token=${webhook.token}`
    : "";

  const copyWebhook = async () => {
    try { await navigator.clipboard.writeText(webhookUrl); toast.success("Webhook URL copied"); }
    catch { toast.error("Could not copy — select and copy manually"); }
  };

  const regenWebhook = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/operator/telematics/webhook-config/regenerate");
      setWebhook(data);
      toast.success("New token generated — update it with your GPS provider");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const savePm = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/operator/payment-methods", {
        default: pm.default,
        credit_enabled: pm.credit?.enabled ?? true,
        razorpay_enabled: pm.razorpay?.enabled ?? false,
        razorpay_key_id: pm.razorpay?.key_id || "",
      });
      setPm(data);
      toast.success("Payment methods updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Branding, live board, geofence & telematics configuration.</p>
      </div>

      {user?.role === "owner" && brand && (
        <section data-testid="branding-settings" className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <h2 className="text-lg font-semibold">Branding & theme</h2>
          <p className="mb-4 text-sm text-muted-foreground">Upload your logo — we use it as your favicon and auto-match the whole app's theme to your brand colour.</p>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Logo</Label>
              <FileUpload testid="branding-logo" label="Upload logo (PNG/JPG)" category="branding" value={brand.logo_file} onChange={uploadLogo} />
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground">Uploading recolours your theme automatically.</p>
                {brand.logo_file && (
                  <button data-testid="branding-remove-logo" onClick={removeLogo} disabled={savingBrand} className="text-xs font-medium text-status-overdue hover:underline">Remove</button>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Brand name</Label><Input data-testid="branding-logo-text" value={brand.logo_text || ""} onChange={(e) => setBrand({ ...brand, logo_text: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>App title</Label><Input data-testid="branding-app-title" value={brand.app_title || ""} onChange={(e) => setBrand({ ...brand, app_title: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Tagline</Label><Input data-testid="branding-tagline" value={brand.tagline || ""} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })} /></div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-tenant-border pt-4">
            <span className="text-xs font-medium text-muted-foreground">Theme</span>
            {["--tenant-primary", "--tenant-accent", "--tenant-background", "--tenant-border"].map((k) => (
              <span key={k} data-testid={`swatch${k}`} className="h-7 w-7 rounded-full border border-tenant-border" style={{ background: `hsl(${brand.tokens?.[k] || "0 0% 90%"})` }} title={k} />
            ))}
            {brand.logo_file && (
              <Button data-testid="branding-recolor" variant="outline" size="sm" onClick={() => uploadLogo(brand.logo_file)} disabled={savingBrand} className="gap-1.5"><Palette className="h-4 w-4" /> Recolour from logo</Button>
            )}
            <Button data-testid="branding-save" onClick={saveBrandText} disabled={savingBrand} className="ml-auto gap-2">{savingBrand && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </div>
        </section>
      )}

      {live && (
        <section data-testid="liveboard-settings" className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <h2 className="text-lg font-semibold">Live board</h2>
          <p className="mb-4 text-sm text-muted-foreground">Turn board features on/off and tune alert thresholds.</p>
          <div className="space-y-2">
            {[["alerts_enabled", "Proactive alerts panel"], ["search_enabled", "Search & filter bar"], ["follow_enabled", "Follow mode"], ["pulse_enabled", "Fleet pulse stats"], ["manual_fetch_enabled", "“Fetch live data” button on live board"]].map(([key, label]) => (
              <label key={key} className="flex items-center justify-between rounded-tenant-button border border-tenant-border px-3 py-2.5 text-sm">
                <span>{label}</span>
                <Checkbox data-testid={`live-${key}`} checked={!!live[key]} disabled={user?.role !== "owner"}
                  onCheckedChange={(v) => user?.role === "owner" && saveLive({ [key]: !!v })} />
              </label>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[["idle_min", "Idle alert (min)"], ["stale_min", "Stale GPS (min)"], ["speed_limit", "Speed limit (km/h)"]].map(([key, label]) => (
              <div key={key} className="space-y-1.5">
                <Label>{label}</Label>
                <Input data-testid={`live-${key}`} type="number" value={live[key]} disabled={user?.role !== "owner"}
                  onChange={(e) => setLive({ ...live, [key]: Number(e.target.value) })} />
              </div>
            ))}
          </div>
          {user?.role === "owner" && (
            <Button data-testid="live-save" onClick={() => saveLive({})} disabled={saving} className="mt-4 gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save thresholds
            </Button>
          )}
          <div className="mt-5 border-t border-tenant-border pt-4">
            <Label className="flex items-center gap-1.5"><MapPinned className="h-4 w-4 text-tenant-primary" /> Default map view</Label>
            <p className="mb-2 mt-1 text-xs text-muted-foreground">Where the live board map opens when you arrive.</p>
            <Select value={live.map_default || "main_hub"} disabled={user?.role !== "owner"} onValueChange={(v) => saveLive({ map_default: v })}>
              <SelectTrigger data-testid="map-default-select" className="max-w-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="main_hub">Main hub</SelectItem>
                {(hubs || []).filter((h) => h.id && !h.is_main).map((h) => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
                <SelectItem value="fleet">Fit all vehicles</SelectItem>
                <SelectItem value="user">My location</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </section>
      )}

      <section className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <h2 className="text-lg font-semibold">Operating geofence</h2>
        <p className="mb-4 text-sm text-muted-foreground">Vehicles outside this zone raise a breach on the live board.</p>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label>Center latitude</Label>
            <Input data-testid="geofence-lat" type="number" value={geo.lat} onChange={(e) => setGeo({ ...geo, lat: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Center longitude</Label>
            <Input data-testid="geofence-long" type="number" value={geo.long} onChange={(e) => setGeo({ ...geo, long: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Radius (km)</Label>
            <Input data-testid="geofence-radius" type="number" value={geo.radius_km} onChange={(e) => setGeo({ ...geo, radius_km: e.target.value })} /></div>
        </div>
        {user?.role === "owner" && (hubs || []).length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Centre on hub:</span>
            {(hubs || []).filter((h) => h.lat != null).map((h, i) => (
              <button key={h.id || i} type="button" data-testid={`geofence-center-hub-${i}`} onClick={() => centerGeofenceOn(h)}
                className="inline-flex items-center gap-1.5 rounded-full border border-tenant-border px-3 py-1 text-xs font-medium text-foreground transition-colors hover:border-tenant-primary hover:text-tenant-primary">
                <Home className="h-3.5 w-3.5" /> {h.name}{h.is_main ? " (main)" : ""}
              </button>
            ))}
          </div>
        )}
        {user?.role === "owner" && (
          <Button data-testid="geofence-save" onClick={saveGeo} disabled={saving} className="mt-4 gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save geofence
          </Button>
        )}
      </section>

      {hubs && (
        <section data-testid="hubs-settings" className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold"><Home className="h-5 w-5 text-tenant-primary" /> Hub locations</h2>
              <p className="mt-1 text-sm text-muted-foreground">Your physical bases / offices. They appear as home markers on the live board — your main hub is highlighted.</p>
            </div>
            {user?.role === "owner" && (
              <Button data-testid="hub-add" variant="outline" size="sm" onClick={addHub} className="gap-1.5"><Plus className="h-4 w-4" /> Add hub</Button>
            )}
          </div>

          <div className="mt-4 space-y-4">
            {(hubs || []).length === 0 && (
              <p className="rounded-tenant-button border border-dashed border-tenant-border px-4 py-6 text-center text-sm text-muted-foreground">No hubs yet. Add your shop or office so operators can see vehicles relative to it.</p>
            )}
            {(hubs || []).map((h, i) => (
              <div key={i} data-testid={`hub-row-${i}`} className="rounded-tenant-button border border-tenant-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button data-testid={`hub-main-${i}`} type="button" disabled={user?.role !== "owner"} onClick={() => user?.role === "owner" && setMainHub(i)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${h.is_main ? "border-tenant-primary bg-tenant-primary/10 text-tenant-primary" : "border-tenant-border text-muted-foreground hover:text-foreground"}`}>
                    <Star className={`h-3.5 w-3.5 ${h.is_main ? "fill-tenant-primary" : ""}`} /> {h.is_main ? "Main hub" : "Set as main"}
                  </button>
                  {user?.role === "owner" && (
                    <button data-testid={`hub-remove-${i}`} type="button" onClick={() => removeHub(i)} className="inline-flex items-center gap-1 text-xs font-medium text-status-overdue hover:underline"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                  )}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5"><Label>Hub name</Label>
                    <Input data-testid={`hub-name-${i}`} value={h.name} disabled={user?.role !== "owner"} onChange={(e) => updateHub(i, { name: e.target.value })} placeholder="e.g. Main Shop" /></div>
                  <div className="space-y-1.5"><Label>Address</Label>
                    <Input data-testid={`hub-address-${i}`} value={h.address || ""} disabled={user?.role !== "owner"} onChange={(e) => updateHub(i, { address: e.target.value })} placeholder="Street, area, city" /></div>
                  <div className="space-y-1.5"><Label>Latitude</Label>
                    <Input data-testid={`hub-lat-${i}`} type="number" value={h.lat} disabled={user?.role !== "owner"} onChange={(e) => updateHub(i, { lat: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Longitude</Label>
                    <Input data-testid={`hub-long-${i}`} type="number" value={h.long} disabled={user?.role !== "owner"} onChange={(e) => updateHub(i, { long: e.target.value })} /></div>
                </div>
              </div>
            ))}
          </div>
          {user?.role === "owner" && (hubs || []).length > 0 && (
            <Button data-testid="hubs-save" onClick={saveHubs} disabled={saving} className="mt-4 gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save hubs
            </Button>
          )}
        </section>
      )}

      {featureEnabled?.("telemetry") && ft && (
        <section data-testid="fleettrack-config" className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold"><Cpu className="h-5 w-5 text-tenant-primary" /> FleetTrack GPS account</h2>
              <p className="mt-1 text-sm text-muted-foreground">Paste the live-data link your GPS provider (gpstrack.in) gave you. We read the token & email from it automatically — no redeploy needed.</p>
            </div>
            <span data-testid="ft-status" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${ft.configured ? "bg-status-available/15 text-status-available" : "bg-muted text-muted-foreground"}`}>
              <Radio className="h-3.5 w-3.5" /> {ft.configured ? "Custom link active" : "Using default credentials"}
            </span>
          </div>

          {ft.configured && (
            <div className="mt-4 grid gap-3 rounded-tenant-button border border-tenant-border bg-tenant-bg/40 p-3 text-sm sm:grid-cols-3" data-testid="ft-current">
              <div><p className="text-xs text-muted-foreground">Endpoint</p><p className="truncate font-mono text-xs">{ft.url}</p></div>
              <div><p className="text-xs text-muted-foreground">Email</p><p className="truncate font-medium">{ft.email}</p></div>
              <div><p className="text-xs text-muted-foreground">Token</p><p className="font-mono">{ft.token_masked}</p></div>
            </div>
          )}

          {user?.role === "owner" ? (
            <div className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <Label>gpstrack live-data link</Label>
                <Input data-testid="ft-link" value={ftLink} onChange={(e) => setFtLink(e.target.value)}
                  placeholder="https://app.gpstrack.in/api/get_current_data?token=…&email=…" className="font-mono text-xs" />
                <p className="text-[11px] text-muted-foreground">We extract the base URL, token and email from the link. It's stored securely per your account.</p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox data-testid="ft-default" checked={ftDefault} onCheckedChange={(v) => setFtDefault(!!v)} />
                Make FleetTrack the active provider for this fleet
              </label>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="ft-save" onClick={saveFleetTrack} disabled={ftSaving} className="gap-2">
                  {ftSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />} Save & test connection
                </Button>
                <Button data-testid="ft-sync" variant="outline" onClick={syncFleet} disabled={ftSaving} className="gap-2">
                  <RefreshCw className="h-4 w-4" /> Sync fleet from live feed
                </Button>
                {ft.configured && (
                  <Button data-testid="ft-clear" variant="outline" onClick={clearFleetTrack} disabled={ftSaving} className="gap-2 text-status-overdue">
                    <Trash2 className="h-4 w-4" /> Revert to default
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">“Sync fleet” pulls every active device from your GPS account and adds any missing vehicles automatically.</p>
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground">Only the owner can change the GPS provider link.</p>
          )}
        </section>
      )}

      {featureEnabled?.("telemetry") && (
        <section data-testid="telematics-diagnostics" className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold"><Database className="h-5 w-5 text-tenant-primary" /> Live data & device diagnostics</h2>
              <p className="mt-1 text-sm text-muted-foreground">See exactly what the GPS feed is returning, so you can tell a working-but-parked device from an actual outage.</p>
            </div>
            <Button data-testid="diag-refresh" variant="outline" size="sm" onClick={() => loadDiag(true)} disabled={diagLoading} className="gap-1.5">
              {diagLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Fetch live now
            </Button>
          </div>

          {!diag && !diagLoading && (
            <p className="mt-4 rounded-tenant-button border border-dashed border-tenant-border px-4 py-6 text-center text-sm text-muted-foreground">No diagnostics loaded yet. Click “Fetch live now”.</p>
          )}

          {diag && (
            <div className="mt-4 space-y-4">
              {Object.entries(diag.providers || {}).filter(([, p]) => p.live_feed).map(([name, p]) => {
                const st = DIAG_STATUS[p.last_fetch_status] || DIAG_STATUS.never;
                return (
                  <div key={name} data-testid={`diag-provider-${name}`} className="rounded-tenant-button border border-tenant-border p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Cpu className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold capitalize">{name}</span>
                      <span data-testid={`diag-status-${name}`} className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${st.cls}`}>{st.label}</span>
                      {!p.configured && <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">Not configured</span>}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                      <div><p className="text-xs text-muted-foreground">Last fetch attempt</p><p className="font-semibold">{fmtAge(p.last_fetch_age_seconds)}</p></div>
                      <div><p className="text-xs text-muted-foreground">Last good data</p><p className="font-semibold">{fmtAge(p.last_good_age_seconds)}</p></div>
                      <div><p className="text-xs text-muted-foreground">Refresh window</p><p className="font-semibold">~{p.refresh_seconds}s</p></div>
                      <div><p className="text-xs text-muted-foreground">Devices in feed</p><p className="font-semibold">{p.cached_record_count}</p></div>
                    </div>
                    {p.last_fetch_detail && <p className="mt-2 text-xs text-status-overdue">{p.last_fetch_detail}</p>}
                  </div>
                );
              })}

              <p className="rounded-tenant-button bg-tenant-primary/5 px-3 py-2 text-xs leading-relaxed text-muted-foreground">{diag.note}</p>

              <div className="overflow-x-auto rounded-tenant-button border border-tenant-border">
                <table className="w-full min-w-[560px] text-left text-sm">
                  <thead className="border-b border-tenant-border bg-tenant-bg/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="px-3 py-2">Vehicle</th><th className="px-3 py-2">Device</th><th className="px-3 py-2">In feed</th><th className="px-3 py-2">Last fix</th><th className="px-3 py-2">Speed</th><th className="px-3 py-2">Ignition</th></tr>
                  </thead>
                  <tbody>
                    {(diag.devices || []).map((d) => (
                      <tr key={d.reg_number} data-testid={`diag-device-${d.reg_number}`} className="border-b border-tenant-border/60 last:border-0">
                        <td className="px-3 py-2 font-medium">{d.reg_number}</td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{d.device_id || "—"}</td>
                        <td className="px-3 py-2">{d.in_live_feed ? <Check className="h-4 w-4 text-status-available" /> : <X className="h-4 w-4 text-status-overdue" />}</td>
                        <td className={`px-3 py-2 ${d.fix_age_seconds != null && d.fix_age_seconds > 600 ? "text-status-overdue" : ""}`}>{fmtAge(d.fix_age_seconds)}</td>
                        <td className="px-3 py-2">{d.speed != null ? `${Math.round(d.speed)} km/h` : "—"}</td>
                        <td className="px-3 py-2">{d.ignition || "—"}</td>
                      </tr>
                    ))}
                    {(diag.devices || []).length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No paired devices.</td></tr>}
                  </tbody>
                </table>
              </div>

              <button data-testid="diag-raw-toggle" type="button" onClick={() => setShowRaw((s) => !s)} className="text-sm font-medium text-tenant-primary hover:underline">
                {showRaw ? "Hide" : "Show"} raw API JSON
              </button>
              {showRaw && (
                <pre data-testid="diag-raw-json" className="max-h-80 overflow-auto rounded-tenant-button border border-tenant-border bg-tenant-bg/40 p-3 text-[11px] leading-relaxed text-foreground">
{JSON.stringify(diag.providers, null, 2)}
                </pre>
              )}
            </div>
          )}
        </section>
      )}

      <section className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <h2 className="text-lg font-semibold">Escalation policy</h2>
        <p className="mb-4 text-sm text-muted-foreground">Hours overdue at which each recovery rung triggers. Tune per your tolerance.</p>
        {esc && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[["warn_hours", "Warning (h)"], ["watch_hours", "Recovery watch (h)"], ["disable_hours", "Remote disable (h)"], ["recovery_hours", "Recovery mode (h)"], ["exposure_trigger", "Exposure trigger"]].map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Input data-testid={`esc-${key}`} type="number" step={key === "exposure_trigger" ? "0.05" : "1"}
                    value={esc[key]} onChange={(e) => setEsc({ ...esc, [key]: e.target.value })} />
                </div>
              ))}
            </div>
            {user?.role === "owner" && (
              <Button data-testid="esc-save" onClick={saveEsc} disabled={saving} className="mt-4 gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save escalation policy
              </Button>
            )}
          </>
        )}
      </section>

      {maint && (
        <section className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <h2 className="text-lg font-semibold">Maintenance policy</h2>
          <p className="mb-4 text-sm text-muted-foreground">Telemetry auto-raises a work order when a vehicle crosses the service interval or hits a deep-discharge battery event.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[["service_interval_km", "Service interval (km)"], ["service_interval_days", "Service interval (days)"], ["deep_discharge_pct", "Deep-discharge (%)"]].map(([key, label]) => (
              <div key={key} className="space-y-1.5">
                <Label>{label}</Label>
                <Input data-testid={`maint-${key}`} type="number" value={maint[key]}
                  onChange={(e) => setMaint({ ...maint, [key]: e.target.value })} />
              </div>
            ))}
          </div>
          {user?.role === "owner" && (
            <Button data-testid="maint-save" onClick={saveMaint} disabled={saving} className="mt-4 gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save maintenance policy
            </Button>
          )}
        </section>
      )}

      {pay && (
        <section className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <h2 className="text-lg font-semibold">UPI payment (tourist self-booking)</h2>
          <p className="mb-4 text-sm text-muted-foreground">Tourists pay this UPI ID via a QR on your public booking page. No gateway, no commission — your counter confirms each payment manually before handover.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>UPI ID (VPA)</Label>
              <Input data-testid="pay-upi-vpa" value={pay.upi_vpa || ""} onChange={(e) => setPay({ ...pay, upi_vpa: e.target.value })} placeholder="yourname@okhdfcbank" /></div>
            <div className="space-y-1.5"><Label>Payee name</Label>
              <Input data-testid="pay-payee-name" value={pay.payee_name || ""} onChange={(e) => setPay({ ...pay, payee_name: e.target.value })} placeholder="VGR Rentals" /></div>
          </div>
          {user?.role === "owner" && (
            <Button data-testid="pay-save" onClick={savePay} disabled={saving} className="mt-4 gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save UPI details
            </Button>
          )}
        </section>
      )}

      {pm && (
        <section data-testid="payment-methods-section" className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <h2 className="text-lg font-semibold">Payment methods</h2>
          <p className="mb-4 mt-1 text-sm text-muted-foreground">
            UPI QR (manual confirm) is the default everywhere — zero gateway commission. Razorpay can be enabled for scale once you add your keys.
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border border-tenant-border px-3 py-2.5">
              <div><p className="text-sm font-medium">UPI QR</p><p className="text-xs text-muted-foreground">Default · always on · no commission</p></div>
              <span className="rounded-full bg-status-available/15 px-2.5 py-1 text-xs font-medium text-status-available">Active{pm.default === "qr" ? " · default" : ""}</span>
            </div>
            <label className="flex items-center justify-between rounded-md border border-tenant-border px-3 py-2.5">
              <div><p className="text-sm font-medium">Credit / NET terms</p><p className="text-xs text-muted-foreground">Invoice now, mark paid later (business fleet)</p></div>
              <Checkbox data-testid="pm-credit" checked={pm.credit?.enabled ?? true} onCheckedChange={(v) => setPm({ ...pm, credit: { enabled: !!v } })} />
            </label>
            <div className="rounded-md border border-tenant-border px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">Razorpay</p><p className="text-xs text-muted-foreground">Card / UPI / netbanking gateway — for scale</p></div>
                {pm.razorpay?.configured
                  ? <Checkbox data-testid="pm-razorpay" checked={pm.razorpay?.enabled ?? false} onCheckedChange={(v) => setPm({ ...pm, razorpay: { ...pm.razorpay, enabled: !!v } })} />
                  : <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">Add keys to enable</span>}
              </div>
              <div className="mt-2 space-y-1.5">
                <Label className="text-xs">Razorpay Key ID</Label>
                <Input data-testid="pm-razorpay-key" value={pm.razorpay?.key_id || ""} placeholder="rzp_live_xxxxxxxx"
                  onChange={(e) => setPm({ ...pm, razorpay: { ...pm.razorpay, key_id: e.target.value } })} />
                <p className="text-[11px] text-muted-foreground">Provide your key to enable Razorpay. The default stays UPI QR until you switch it.</p>
              </div>
            </div>
          </div>
          {user?.role === "owner" && (
            <Button data-testid="pm-save" onClick={savePm} disabled={saving} className="mt-4 gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save payment methods
            </Button>
          )}
        </section>
      )}

      {webhook && (
        <section data-testid="webhook-section" className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Real-time tracking (push)</h2>
            {webhook.realtime_active ? (
              <span data-testid="webhook-status" className="inline-flex items-center gap-1.5 rounded-full bg-status-available/15 px-2.5 py-1 text-xs font-medium text-status-available"><Wifi className="h-3.5 w-3.5" /> Push active</span>
            ) : (
              <span data-testid="webhook-status" className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"><WifiOff className="h-3.5 w-3.5" /> Polling (~30s)</span>
            )}
          </div>
          <p className="mb-4 mt-1 text-sm text-muted-foreground">
            Your GPS provider's read API is rate-limited to one call every 30s. For true real-time (sub-second) updates, ask <span className="font-medium">{webhook.provider}</span> to <span className="font-medium">PUSH</span> device updates to the URL below. The moment pushes arrive, the live board switches to real-time automatically and stops polling.
          </p>
          <div className="space-y-1.5">
            <Label>Webhook URL — give this to your GPS provider</Label>
            <div className="flex gap-2">
              <Input data-testid="webhook-url" readOnly value={webhookUrl} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
              <Button data-testid="webhook-copy" type="button" variant="outline" size="icon" onClick={copyWebhook}><Copy className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Method <span className="font-mono">POST</span> · JSON body (same record shape as the live feed) · the <span className="font-mono">token</span> authenticates the push.
            </p>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="webhook-stats">
            <div className="rounded-tenant-button border border-tenant-border p-3">
              <p className="text-xs text-muted-foreground">Pushes received</p>
              <p className="mt-0.5 text-lg font-semibold">{webhook.received_count ?? 0}</p>
            </div>
            <div className="rounded-tenant-button border border-tenant-border p-3">
              <p className="text-xs text-muted-foreground">Successfully ingested</p>
              <p className="mt-0.5 text-lg font-semibold">{webhook.push_count ?? 0}</p>
            </div>
            <div className="rounded-tenant-button border border-tenant-border p-3">
              <p className="text-xs text-muted-foreground">Last push</p>
              <p className="mt-0.5 text-sm font-medium">{webhook.last_received_at ? fmtAge(_ageSec(webhook.last_received_at)) : "never"}</p>
            </div>
            <div className="rounded-tenant-button border border-tenant-border p-3">
              <p className="text-xs text-muted-foreground">Last result</p>
              <p className={`mt-0.5 truncate text-sm font-medium ${webhook.last_reason === "ok" ? "text-status-available" : webhook.last_reason ? "text-status-overdue" : ""}`}>{webhook.last_reason || "—"}</p>
            </div>
          </div>

          {webhook.received_count > 0 && webhook.push_count === 0 && (
            <p className="mt-3 rounded-tenant-button bg-status-overdue/10 px-3 py-2 text-xs text-status-overdue" data-testid="webhook-warning">
              Pushes are arriving but none ingested. Reason: <span className="font-mono">{webhook.last_reason}</span>.
              {String(webhook.last_reason || "").startsWith("device_not_paired") && " That device isn't paired to a vehicle in this fleet — run “Sync fleet from live feed”."}
              {webhook.last_reason === "unparseable" && " The payload shape isn't recognised — check the sample below and share it with us."}
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button data-testid="webhook-refresh" type="button" onClick={() => api.get("/operator/telematics/webhook-config").then(({ data }) => setWebhook(data))} className="text-sm font-medium text-tenant-primary hover:underline">
              Refresh push status
            </button>
            {webhook.last_payload && (
              <button data-testid="webhook-payload-toggle" type="button" onClick={() => setShowPush((s) => !s)} className="text-sm font-medium text-tenant-primary hover:underline">
                {showPush ? "Hide" : "Show"} last received payload
              </button>
            )}
          </div>
          {showPush && webhook.last_payload && (
            <pre data-testid="webhook-payload" className="mt-2 max-h-60 overflow-auto rounded-tenant-button border border-tenant-border bg-tenant-bg/40 p-3 text-[11px] leading-relaxed text-foreground">{webhook.last_payload}</pre>
          )}
          {user?.role === "owner" && (
            <Button data-testid="webhook-regenerate" variant="outline" onClick={regenWebhook} disabled={saving} className="mt-4 gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate token
            </Button>
          )}
        </section>
      )}

      <section className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <h2 className="text-lg font-semibold">Telematics providers</h2>
        <p className="mb-4 text-sm text-muted-foreground">Pluggable per vehicle on the Fleet page. The platform talks only to the provider port.</p>
        <div className="overflow-hidden rounded-lg border border-tenant-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr><th className="px-3 py-2 font-medium">Provider</th><th className="px-3 py-2 font-medium">Location</th>
                <th className="px-3 py-2 font-medium">Charge</th><th className="px-3 py-2 font-medium">Odometer</th>
                <th className="px-3 py-2 font-medium">Remote disable</th><th className="px-3 py-2 font-medium">Push</th>
                <th className="px-3 py-2 font-medium">Status</th></tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.name} data-testid={`provider-row-${p.name}`} className="border-t border-tenant-border">
                  <td className="px-3 py-2 font-medium capitalize">{p.name}</td>
                  <td className="px-3 py-2"><Cap ok={p.capabilities.location} /></td>
                  <td className="px-3 py-2"><Cap ok={p.capabilities.charge} /></td>
                  <td className="px-3 py-2"><Cap ok={p.capabilities.odometer} /></td>
                  <td className="px-3 py-2"><Cap ok={p.capabilities.remote_disable} /></td>
                  <td className="px-3 py-2"><Cap ok={p.capabilities.push} /></td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-xs ${p.configured ? "text-status-available" : "text-muted-foreground"}`}>
                      <Radio className="h-3 w-3" /> {p.configured ? "ready" : "needs credentials"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
